import React from "react";
import { Info } from "react-feather";
import Select from "react-select";

const InfoBox = ({ children }) => (
  <div className="bg-blue-50 p-4 rounded-lg border border-blue-100 flex items-start mt-4">
    <Info size={20} className="text-blue-500 mr-3 mt-0.5 flex-shrink-0" />
    <p className="text-sm text-blue-700">{children}</p>
  </div>
);

const TenantInfoStep = ({
  form,
  setForm,
  options,
  errors,
  onBlur,
  checklistOptions,
  checklistSelection,
  setChecklistSelection,
  setValidationErrors,
}) => {
  const {
    npTypeOptions,
    buyerDomainOptions,
    sellerDomainOptions,
    buyerProductOptions,
    sellerProductOptions,
    env,
  } = options;

  const handleNestedFieldChange = (objectKey, fieldName, fieldValue) => {
    setForm((prev) => ({
      ...prev,
      [objectKey]: {
        ...prev[objectKey],
        [fieldName]: fieldValue,
      },
    }));
  };

  const handleFieldChange = (e) => {
    const { name, value, multiple, options } = e.target;

    setForm((prev) => {
      let newValue;
      if (multiple) {
        newValue = Array.from(options)
          .filter((opt) => opt.selected)
          .map((opt) => opt.value);
      } else {
        newValue = value;
      }

      const newForm = { ...prev, [name]: newValue };

      if (name === "npType") {
        newForm.npDomain = "";
        newForm.services = [];
      }

      if (name === "npDomain") {
        newForm.services = [];
        if (value !== "Retail - ISN" && value !== "Retail - MSN") {
          newForm.retailDomains = [];
        }
      }

      return newForm;
    });
  };

  const handleCheckboxChange = (name, value) => {
    setForm((prev) => {
      let newServices = [...prev[name]];

      if (value === "ISN" || value === "MSN") {
        if (newServices.includes("ISN") && value === "MSN") {
          newServices = newServices.filter((v) => v !== "ISN");
        } else if (newServices.includes("MSN") && value === "ISN") {
          newServices = newServices.filter((v) => v !== "MSN");
        }

        if (!newServices.includes(value)) {
          newServices.push(value);
        } else {
          newServices = newServices.filter((v) => v !== value);
        }
      } else {
        newServices = newServices.includes(value)
          ? newServices.filter((v) => v !== value)
          : [...newServices, value];
      }

      // ✅ Clear services error when user selects/deselects products
      if (name === "services" && newServices.length > 0) {
        setValidationErrors((prevErr) => ({ ...prevErr, services: "" }));
      }

      return { ...prev, [name]: newServices };
    });
  };

  const handleChecklistSelection = (key) => {
    setChecklistSelection((prev) => {
      // 1. Identify the item being toggled
      const currentItem = prev.find((item) => item.sKey === key);
      if (!currentItem) return prev;

      const isTurningOn = !currentItem.bValue;

      // 2. Count how many are currently selected (true)
      const currentTrueCount = prev.filter((item) => item.bValue).length;

      // 3. Apply Validation Rules for "Selecting" (Turning ON)
      if (isTurningOn) {
        const totalOptions = prev.length;

        // RULE: Allow selecting all options or any combination as long as at least one is selected
        // No restriction on how many can be selected
        // Just ensure at least one option is selected during final validation
      }

      // 4. If we reach here, the selection is valid. Clear errors and update.
      setValidationErrors((prevErr) => ({ ...prevErr, checklist: "" }));

      return prev.map((item) =>
        item.sKey === key ? { ...item, bValue: isTurningOn } : item,
      );
    });
  };

  const isBuyerApp = form.npType === "Buyer App";
  const isSellerApp = form.npType === "Seller App";
  const showDomain = !!form.npType;
  const showProduct = !!form.npDomain;
  const showIsnOptions = form.services.includes("ISN");
  const showMsnOptions = form.services.includes("MSN");
  const showSettlementFields = form.services.includes("RSF");
  const showIgmFields = form.services.includes("IGM");

  const domainOptions = isBuyerApp ? buyerDomainOptions : sellerDomainOptions;
  const productOptions =
    isSellerApp && form.npDomain && sellerProductOptions
      ? sellerProductOptions[form.npDomain] || []
      : buyerProductOptions;

  const defaultKeysMap = (options.default_keys || []).reduce((acc, item) => {
    if (item?.key_name) {
      acc[item.key_name] = item.key_value || "";
    }
    return acc;
  }, {});

  const isNoKeysLocked = !!defaultKeysMap.no_keys;
  const isWebhookLocked = !!defaultKeysMap.np_communication_webhook;
  const isSettlementAgencyKeyLocked =
    !!defaultKeysMap.settlement_agency_public_key;
  const isSubscriberPrivateIdLocked = !!defaultKeysMap.subscriber_private_id;
  const isUniqueIdLocked = !!defaultKeysMap.unique_id;
  const isSigningPublicIdLocked = !!defaultKeysMap.signing_public_id;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Merchant Name <span className="text-red-500">*</span>
          </label>
          <input
            name="userName"
            aria-required="true"
            required
            value={form.userName}
            onChange={(e) => {
              handleFieldChange(e);
              // Clear API error when user modifies userName
              if (errors?.userName) {
                setValidationErrors((prev) => {
                  const updated = { ...prev };
                  delete updated.userName;
                  delete updated.apiError;
                  return updated;
                });
              }
            }}
            onBlur={() => onBlur("userName")}
            placeholder="Merchant name"
            className={`w-full px-4 py-2 border ${errors?.userName ? "border-red-500" : "border-gray-300"} rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200`}
          />
          {errors?.userName && (
            <p className="text-sm text-red-600 mt-1 font-medium flex items-center gap-1">
              <span className="inline-block w-1 h-1 bg-red-600 rounded-full"></span>
              {errors.userName}
            </p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Merchant Email <span className="text-red-500">*</span>
          </label>
          <input
            name="email"
            type="email"
            aria-required="true"
            required
            value={form.email}
            onChange={(e) => {
              handleFieldChange(e);
              // Clear API error when user modifies email
              if (errors?.email) {
                setValidationErrors((prev) => {
                  const updated = { ...prev };
                  delete updated.email;
                  delete updated.apiError;
                  return updated;
                });
              }
            }}
            onBlur={() => onBlur("email")}
            placeholder="merchant@company.com"
            className={`w-full px-4 py-2 border ${errors?.email ? "border-red-500" : "border-gray-300"} rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200`}
          />
          {errors?.email && (
            <p className="text-sm text-red-600 mt-1 font-medium flex items-center gap-1">
              <span className="inline-block w-1 h-1 bg-red-600 rounded-full"></span>
              {errors.email}
            </p>
          )}
        </div>

      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          NP Types <span className="text-red-500">*</span>
        </label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {npTypeOptions.map((opt) => (
            <label
              key={opt.value}
              className={`flex items-center px-4 py-3 rounded-lg border cursor-pointer transition-all duration-200 ${
                form.npType === opt.value
                  ? "bg-blue-50 border-blue-500 text-blue-700 shadow-sm"
                  : "bg-white border-gray-200 hover:bg-gray-50"
              }`}
            >
              <input
                type="radio"
                name="npType"
                value={opt.value}
                checked={form.npType === opt.value}
                onChange={handleFieldChange}
                onBlur={() => onBlur("npType")}
                className="sr-only"
                aria-required="true"
                required
              />
              <span className="ml-1 select-none">{opt.label}</span>
            </label>
          ))}
          {errors?.npType && (
            <p className="text-sm text-red-600 mt-1">{errors.npType}</p>
          )}
        </div>
      </div>

      {showDomain && domainOptions && (
        <>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Domain <span className="text-red-500">*</span>
            </label>
            <select
              name="npDomain"
              value={form.npDomain}
              onChange={handleFieldChange}
              onBlur={() => onBlur("npDomain")}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
            >
              <option value="">Select Domain</option>
              {domainOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            {errors?.npDomain && (
              <p className="text-sm text-red-600 mt-1">{errors.npDomain}</p>
            )}
          </div>
        </>
      )}
      {showDomain && form.npDomain === "Retail - MSN Provider" && (
        <div className="space-y-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
          <h4 className="text-sm font-semibold text-gray-700">
            MSN Fee Configuration
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Fee Type Dropdown */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Fee Type<span className="text-red-500">*</span>
              </label>
              <select
                name="msnFeeType"
                value={form.msnFee.type}
                onChange={(e) => {
                  const selectedType = options.commissionFeeTypes.find(
                    (t) => t.value === e.target.value,
                  );
                  setForm((prev) => ({
                    ...prev,
                    msnFee: {
                      ...prev.msnFee,
                      type: e.target.value,
                      valueType: selectedType ? selectedType.valueType : "",
                    },
                  }));
                }}
                onBlur={() => onBlur("msnFee")}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select Fee Type</option>
                {options.commissionFeeTypes?.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.type} ({opt.valueType === "percent" ? "%" : "Fixed"})
                  </option>
                ))}
              </select>
            </div>

            {/* Fee Amount Input */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Fee Amount {form.msnFee.valueType === "percent" ? "(%)" : "(₹)"}{" "}
                <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                name="msnFeeAmount"
                value={form.msnFee.amount}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    msnFee: { ...prev.msnFee, amount: e.target.value },
                  }))
                }
                onBlur={() => onBlur("msnFee")}
                placeholder="0.00"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {errors?.msnFee && (
            <p className="text-sm text-red-600 mt-1">
              {typeof errors.msnFee === "string"
                ? errors.msnFee
                : Object.values(errors.msnFee || {})
                    .filter(Boolean)
                    .join(" ")}
            </p>
          )}
        </div>
      )}
      {showDomain &&
        (form.npDomain === "Retail - ISN" ||
          form.npDomain === "Retail - MSN") && (
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Does Merchant have a Seller App live on ONDC?{" "}
              <span className="text-red-500">*</span>
            </label>
            <div className="flex space-x-4">
              {["Yes", "No"].map((value) => (
                <label
                  key={value}
                  className={`flex items-center px-4 py-2 rounded-lg border cursor-pointer ${
                    form.hasSellerApp === value
                      ? "bg-blue-50 border-blue-500 text-blue-700 shadow-sm"
                      : "bg-white border-gray-200 hover:bg-gray-50"
                  }`}
                >
                  <input
                    type="radio"
                    name="hasSellerApp"
                    value={value}
                    checked={form.hasSellerApp === value}
                    onChange={handleFieldChange}
                    onBlur={() => onBlur("hasSellerApp")}
                    className="sr-only"
                  />
                  <span className="select-none">{value}</span>
                </label>
              ))}
            </div>
            {errors?.hasSellerApp && (
              <p className="text-sm text-red-600 mt-1">{errors.hasSellerApp}</p>
            )}
          </div>
        )}

      {(form.npDomain === "Retail - ISN" || form.npDomain === "Retail - MSN") &&
        form.hasSellerApp === "No" && (
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Include Seller App in onboarding?{" "}
              <span className="text-red-500">*</span>
            </label>
            <div className="flex space-x-4">
              {["Yes", "No"].map((value) => (
                <label
                  key={value}
                  className={`flex items-center px-4 py-2 rounded-lg border cursor-pointer ${
                    form.withSellerApp === value
                      ? "bg-blue-50 border-blue-500 text-blue-700 shadow-sm"
                      : "bg-white border-gray-200 hover:bg-gray-50"
                  }`}
                >
                  <input
                    type="radio"
                    name="withSellerApp"
                    value={value}
                    checked={form.withSellerApp === value}
                    onChange={handleFieldChange}
                    onBlur={() => onBlur("withSellerApp")}
                    className="sr-only"
                  />
                  <span className="select-none">{value}</span>
                </label>
              ))}
            </div>
            {errors?.withSellerApp && (
              <p className="text-sm text-red-600 mt-1">
                {errors.withSellerApp}
              </p>
            )}
          </div>
        )}

      {showDomain && (
        <>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Stages <span className="text-red-500">*</span>
            </label>
            <select
              name="stages"
              value={form.stages}
              onChange={handleFieldChange}
              onBlur={() => onBlur("stages")}
              disabled={form.hasSellerApp === "Yes"}
              className={`w-full px-4 py-2 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 ${
                form.hasSellerApp === "Yes" ? "opacity-80 cursor-not-allowed" : ""
              }`}
            >
              <option value="">Select Stage</option>
              {env.map((opt) => (
                <option key={opt.id} value={opt.id}>
                  {opt.environment_name}
                </option>
              ))}
            </select>
            {form.hasSellerApp === "Yes" && (
              <p className="text-xs text-gray-500 mt-1 italic">
                Automatically set to Production for merchants with live Seller App
              </p>
            )}
            {errors?.stages && (
              <p className="text-sm text-red-600 mt-1">{errors.stages}</p>
            )}
          </div>
        </>
      )}
      {(form.npDomain === "Retail - MSN Provider" ||
        form.npDomain === "Retail - ISN" ||
        form.npDomain === "Retail - MSN") && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            NO Keys{" "}
            {(() => {
              const selectedEnv = options.env?.find(
                (env) => String(env.id) === String(form.stages)
              );
              const isStaging =
                selectedEnv?.environment_name?.toLowerCase().includes("staging");
              return !isStaging ? <span className="text-red-500 ml-1">*</span> : null;
            })()}
          </label>
          <input
            name="nokey"
            value={form.nokey}
            onChange={handleFieldChange}
            onBlur={() => onBlur("nokey")}
            disabled={isNoKeysLocked}
            placeholder="NO Key"
            className={`w-full px-4 py-2 border border-gray-300 rounded-lg transition-all duration-200 ${
              isNoKeysLocked
                ? "bg-gray-100 cursor-not-allowed"
                : "focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            }`}
          />
          {errors?.nokey && (
            <p className="text-sm text-red-600 mt-1">{errors.nokey}</p>
          )}
        </div>
      )}

      {form.npDomain && form.stages && checklistOptions?.length > 0 && (
        <div className="mt-4" id="checklist">
          <label className="block text-sm font-semibold text-gray-700 mb-3">
            Checklist <span className="text-red-500">*</span>
          </label>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {checklistOptions.map((opt) => {
              const isSelected = checklistSelection.find(
                (i) => i.sKey === opt.key,
              )?.bValue;

              return (
                <label
                  key={opt.key}
                  className={`
              relative flex flex-col justify-between h-18 p-3 rounded-xl border-2 transition-all duration-200 cursor-pointer
              ${
                isSelected
                  ? "border-blue-500 bg-blue-50 shadow-sm"
                  : "border-gray-100 bg-white hover:border-gray-300"
              }
            `}
                >
                  {/* Top Row: Checkbox pushed to the right */}
                  <div className="flex justify-end w-full">
                    <input
                      type="checkbox"
                      className="h-5 w-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                      checked={!!isSelected}
                      onChange={() => handleChecklistSelection(opt.key)}
                    />
                  </div>

                  {/* Bottom Row: Text content at the bottom left */}
                  <span
                    className={`text-sm font-medium leading-tight select-none ${
                      isSelected ? "text-blue-900" : "text-gray-700"
                    }`}
                  >
                    {opt.name}
                  </span>
                </label>
              );
            })}
          </div>

          {/* Error message stays aligned below the grid */}
          {errors?.checklist && (
            <p className="text-sm text-red-600 mt-2 font-medium flex items-center gap-1">
              <span className="inline-block w-1 h-1 bg-red-600 rounded-full"></span>
              {errors.checklist}
            </p>
          )}
        </div>
      )}
      {showProduct && productOptions && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Choose Product(s) <span className="text-red-500">*</span>
          </label>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {productOptions
              .filter((opt) => opt.type !== "checkbox") // ✅ skip type=checkbox
              .map((opt) => {
                // Lock RSF & IGM ONLY for "Retail - MSN Provider" (MarketConnect)
                const isMarketConnectMSN =
                  form.npDomain === "Retail - MSN Provider";
                const isLockedOption =
                  isMarketConnectMSN &&
                  (opt.value === "RSF" || opt.value === "IGM");

                return (
                  <label
                    key={opt.value}
                    className={`flex items-center justify-center px-4 py-3 rounded-lg border cursor-pointer transition-all duration-200 text-center ${
                      form.services.includes(opt.value)
                        ? "bg-blue-50 border-blue-500 text-blue-700 shadow-sm"
                        : "bg-white border-gray-200 hover:bg-gray-50"
                    } ${isLockedOption ? "opacity-75" : ""}`}
                  >
                    <input
                      type="checkbox"
                      className="sr-only"
                      checked={form.services.includes(opt.value)}
                      onChange={() => {
                        // Prevent unchecking locked options
                        if (
                          isLockedOption &&
                          form.services.includes(opt.value)
                        ) {
                          return;
                        }
                        handleCheckboxChange("services", opt.value);
                      }}
                      onBlur={() => onBlur("services")}
                      disabled={isLockedOption}
                      aria-disabled={isLockedOption}
                    />
                    <span className="ml-1 select-none">{opt.label}</span>
                    {isLockedOption && (
                      <span className="ml-2 text-xs text-gray-500">
                        (required)
                      </span>
                    )}
                  </label>
                );
              })}
          </div>
          {errors?.services && (
            <p className="text-sm text-red-600 mt-2 font-medium flex items-center gap-1">
              <span className="inline-block w-1 h-1 bg-red-600 rounded-full"></span>
              {errors.services}
            </p>
          )}
        </div>
      )}
      {/* 
      {(form.npDomain === "Retail - ISN" ||
        form.npDomain === "Retail - MSN") && ( */}
      <div className="mt-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Seller Retail Domains <span className="text-red-500">*</span>
        </label>
        <Select
          isMulti
          name="retailDomains"
          aria-required="true"
          required
          options={options.SellerRetailDomainsOptions}
          value={options.SellerRetailDomainsOptions?.filter((opt) =>
            form.retailDomains?.includes(opt.value),
          )}
          onChange={(selected) => {
            const selectedValues = selected
              ? selected.map((opt) => opt.value)
              : [];
            setForm((prev) => ({
              ...prev,
              retailDomains: selectedValues,
            }));
            onBlur("retailDomains");
          }}
          onBlur={() => onBlur("retailDomains")}
          className="basic-multi-select"
          classNamePrefix="select"
          placeholder="Select Seller Retail Domains"
        />

        {errors?.retailDomains && (
          <p className="text-sm text-red-600 mt-1">{errors.retailDomains}</p>
        )}
      </div>
      {/* )} */}

      {showIsnOptions && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Seller App Options
          </label>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { value: "Catalogue Rejection", label: "Catalogue Rejection" },
              { value: "Logs Monitoring", label: "Logs Monitoring" },
            ].map((opt) => (
              <label
                key={opt.value}
                className={`flex items-center justify-center px-4 py-3 rounded-lg border cursor-pointer transition-all duration-200 text-center ${
                  form.services.includes(opt.value)
                    ? "bg-blue-50 border-blue-500 text-blue-700 shadow-sm"
                    : "bg-white border-gray-200 hover:bg-gray-50"
                }`}
              >
                <input
                  type="checkbox"
                  className="sr-only"
                  checked={form.services.includes(opt.value)}
                  onChange={() => handleCheckboxChange("services", opt.value)}
                  onBlur={() => onBlur("services")}
                />
                <span className="ml-1 select-none">{opt.label}</span>
              </label>
            ))}
          </div>
          {errors?.services && (
            <p className="text-sm text-red-600 mt-1">{errors.services}</p>
          )}
        </div>
      )}

      {showMsnOptions && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Seller App Options
          </label>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { value: "Catalogue Rejection", label: "Catalogue Rejection" },
              { value: "Logs Monitoring", label: "Logs Monitoring" },
            ].map((opt) => (
              <label
                key={opt.value}
                className={`flex items-center justify-center px-4 py-3 rounded-lg border cursor-pointer transition-all duration-200 text-center ${
                  form.services.includes(opt.value)
                    ? "bg-blue-50 border-blue-500 text-blue-700 shadow-sm"
                    : "bg-white border-gray-200 hover:bg-gray-50"
                }`}
              >
                <input
                  type="checkbox"
                  className="sr-only"
                  checked={form.services.includes(opt.value)}
                  onChange={() => handleCheckboxChange("services", opt.value)}
                  onBlur={() => onBlur("services")}
                />
                <span className="ml-1 select-none">{opt.label}</span>
              </label>
            ))}
          </div>
          {errors?.services && (
            <p className="text-sm text-red-600 mt-1">{errors.services}</p>
          )}
        </div>
      )}

      <div className="mt-4">
        {/* ✅ Shiprocket Email */}
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Shiprocket Email{" "}
          {(() => {
            const selectedEnv = options.env?.find(
              (env) => String(env.id) === String(form.stages)
            );
            const isStaging =
              selectedEnv?.environment_name?.toLowerCase().includes("staging");
            return !isStaging ? <span className="text-red-500">*</span> : null;
          })()}
        </label>
        <input
          type="email"
          name="shiprocketEmail"
          aria-required="true"
          required
          value={form.shiprocketEmail}
          onChange={handleFieldChange}
          onBlur={() => onBlur("shiprocketEmail")}
          placeholder="Enter Shiprocket Email"
          className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
        />
        {errors?.shiprocketEmail && (
          <p className="text-sm text-red-600 mt-1">{errors.shiprocketEmail}</p>
        )}

        {/* ✅ Shiprocket Password */}
        <label className="block text-sm font-medium text-gray-700 mb-1 mt-4">
          Shiprocket Password{" "}
          {(() => {
            const selectedEnv = options.env?.find(
              (env) => String(env.id) === String(form.stages)
            );
            const isStaging =
              selectedEnv?.environment_name?.toLowerCase().includes("staging");
            return !isStaging ? <span className="text-red-500">*</span> : null;
          })()}
        </label>
        <input
          type="password"
          name="shiprocketPassword"
          aria-required="true"
          required
          value={form.shiprocketPassword || ""}
          onChange={handleFieldChange}
          onBlur={() => onBlur("shiprocketPassword")}
          placeholder="Enter Shiprocket Password"
          autoComplete="new-password"
          className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
        />

        {errors?.shiprocketPassword && (
          <p className="text-sm text-red-600 mt-1">
            {errors.shiprocketPassword}
          </p>
        )}
      </div>

      <div className="mt-4">
        {/* ✅ Nimbus Email */}
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Nimbus Email{" "}
          {(() => {
            const selectedEnv = options.env?.find(
              (env) => String(env.id) === String(form.stages)
            );
            const isStaging =
              selectedEnv?.environment_name?.toLowerCase().includes("staging");
            return !isStaging ? <span className="text-red-500">*</span> : null;
          })()}
        </label>
        <input
          type="email"
          name="nimbusEmail"
          aria-required="true"
          required
          value={form.nimbusEmail}
          onChange={handleFieldChange}
          onBlur={() => onBlur("nimbusEmail")}
          placeholder="Enter Nimbus Email"
          className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
        />
        {errors?.nimbusEmail && (
          <p className="text-sm text-red-600 mt-1">{errors.nimbusEmail}</p>
        )}

        {/* ✅ Nimbus Password */}
        <label className="block text-sm font-medium text-gray-700 mb-1 mt-4">
          Nimbus Password{" "}
          {(() => {
            const selectedEnv = options.env?.find(
              (env) => String(env.id) === String(form.stages)
            );
            const isStaging =
              selectedEnv?.environment_name?.toLowerCase().includes("staging");
            return !isStaging ? <span className="text-red-500">*</span> : null;
          })()}
        </label>
        <input
          type="password"
          name="nimbusPassword"
          aria-required="true"
          required
          value={form.nimbusPassword || ""}
          onChange={handleFieldChange}
          onBlur={() => onBlur("nimbusPassword")}
          placeholder="Enter Nimbus Password"
          autoComplete="new-password"
          className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
        />

        {errors?.nimbusPassword && (
          <p className="text-sm text-red-600 mt-1">
            {errors.nimbusPassword}
          </p>
        )}
      </div>

      {showIgmFields && (
        <div className="space-y-6">
          {/* --- Customer Support --- */}
          <div className="p-4 border rounded-lg space-y-4">
            <h3 className="text-md font-semibold text-gray-800">
              Customer Support
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Name<span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={form.customer_support.name}
                  onChange={(e) =>
                    handleNestedFieldChange(
                      "customer_support",
                      "name",
                      e.target.value,
                    )
                  }
                  onBlur={() => onBlur("customer_support")}
                  placeholder="Enter name"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                {errors?.customer_support?.name && (
                  <p className="text-sm text-red-600 mt-1">
                    {typeof errors.customer_support?.name === "string"
                      ? errors.customer_support.name
                      : "Invalid input"}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  value={form.customer_support.email}
                  onChange={(e) =>
                    handleNestedFieldChange(
                      "customer_support",
                      "email",
                      e.target.value,
                    )
                  }
                  onBlur={() => onBlur("customer_support")}
                  placeholder="Enter email"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                {errors?.customer_support?.email && (
                  <p className="text-sm text-red-600 mt-1">
                    {typeof errors.customer_support?.email === "string"
                      ? errors.customer_support.email
                      : "Invalid input"}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  value={form.customer_support.phone}
                  onChange={(e) =>
                    handleNestedFieldChange(
                      "customer_support",
                      "phone",
                      e.target.value,
                    )
                  }
                  onBlur={() => onBlur("customer_support")}
                  placeholder="Enter phone"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                {errors?.customer_support?.phone && (
                  <p className="text-sm text-red-600 mt-1">
                    {typeof errors.customer_support?.phone === "string"
                      ? errors.customer_support.phone
                      : "Invalid input"}
                  </p>
                )}
              </div>
            </div>

            {/* --- GRO Support --- */}
            <h3 className="text-md font-semibold text-gray-800">GRO Support</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={form.gro_support.name}
                  onChange={(e) =>
                    handleNestedFieldChange(
                      "gro_support",
                      "name",
                      e.target.value,
                    )
                  }
                  onBlur={() => onBlur("gro_support")}
                  placeholder="Enter name"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                {errors?.gro_support?.name && (
                  <p className="text-sm text-red-600 mt-1">
                    {typeof errors.gro_support?.name === "string"
                      ? errors.gro_support.name
                      : "Invalid input"}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  value={form.gro_support.email}
                  onChange={(e) =>
                    handleNestedFieldChange(
                      "gro_support",
                      "email",
                      e.target.value,
                    )
                  }
                  onBlur={() => onBlur("gro_support")}
                  placeholder="Enter email"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                {errors?.gro_support?.email && (
                  <p className="text-sm text-red-600 mt-1">
                    {typeof errors.gro_support?.email === "string"
                      ? errors.gro_support.email
                      : "Invalid input"}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  value={form.gro_support.phone}
                  onChange={(e) =>
                    handleNestedFieldChange(
                      "gro_support",
                      "phone",
                      e.target.value,
                    )
                  }
                  onBlur={() => onBlur("gro_support")}
                  placeholder="Enter phone"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                {errors?.gro_support?.phone && (
                  <p className="text-sm text-red-600 mt-1">
                    {typeof errors.gro_support?.phone === "string"
                      ? errors.gro_support.phone
                      : "Invalid input"}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* --- GRO Support --- */}
          {/* <div className="p-4 border rounded-lg space-y-4">
            <h3 className="text-md font-semibold text-gray-800">GRO Support</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={form.gro_support.name}
                  onChange={(e) =>
                    handleNestedFieldChange(
                      "gro_support",
                      "name",
                      e.target.value,
                    )
                  }
                  onBlur={() => onBlur("gro_support")}
                  placeholder="Enter name"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                {errors?.gro_support?.name && (
                  <p className="text-sm text-red-600 mt-1">
                    {typeof errors.gro_support?.name === "string"
                      ? errors.gro_support.name
                      : "Invalid input"}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  value={form.gro_support.email}
                  onChange={(e) =>
                    handleNestedFieldChange(
                      "gro_support",
                      "email",
                      e.target.value,
                    )
                  }
                  onBlur={() => onBlur("gro_support")}
                  placeholder="Enter email"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                {errors?.gro_support?.email && (
                  <p className="text-sm text-red-600 mt-1">
                    {typeof errors.gro_support?.email === "string"
                      ? errors.gro_support.email
                      : "Invalid input"}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  value={form.gro_support.phone}
                  onChange={(e) =>
                    handleNestedFieldChange(
                      "gro_support",
                      "phone",
                      e.target.value,
                    )
                  }
                  onBlur={() => onBlur("gro_support")}
                  placeholder="Enter phone"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                {errors?.gro_support?.phone && (
                  <p className="text-sm text-red-600 mt-1">
                    {typeof errors.gro_support?.phone === "string"
                      ? errors.gro_support.phone
                      : "Invalid input"}
                  </p>
                )}
              </div>
            </div>
          </div> */}
        </div>
      )}

      {showSettlementFields && (
        <div className="p-4 border rounded-lg space-y-4">
          <h3 className="text-md font-semibold text-gray-800">
            Settlement Configuration
          </h3>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              NP Communication Webhook <span className="text-red-500">*</span>
            </label>
            <input
              name="webhook"
              aria-required="true"
              required
              value={form.webhook}
              onChange={handleFieldChange}
              onBlur={() => onBlur("webhook")}
              disabled={isWebhookLocked}
              placeholder="https://"
              className={`w-full px-4 py-2 border border-gray-300 rounded-lg transition-all duration-200 ${
                isWebhookLocked
                  ? "bg-gray-100 cursor-not-allowed"
                  : "focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              }`}
            />
            {errors?.webhook && (
              <p className="text-sm text-red-600 mt-1">{errors.webhook}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Settlement Agency Public Key{" "}
              <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="settlement_agency_public_key"
              aria-required="true"
              required
              value={form.settlement_agency_public_key}
              onChange={handleFieldChange}
              onBlur={() => onBlur("settlement_agency_public_key")}
              disabled={isSettlementAgencyKeyLocked}
              placeholder="Enter Public Key"
              className={`w-full px-4 py-2 border border-gray-300 rounded-lg transition-all duration-200 ${
                isSettlementAgencyKeyLocked
                  ? "bg-gray-100 cursor-not-allowed"
                  : "focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              }`}
            />
            {errors?.settlement_agency_public_key && (
              <p className="text-sm text-red-600 mt-1">
                {errors.settlement_agency_public_key}
              </p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Subscriber Private Id <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="np_subscriber_private_id"
              aria-required="true"
              required
              value={form.np_subscriber_private_id}
              onChange={handleFieldChange}
              onBlur={() => onBlur("np_subscriber_private_id")}
              disabled={isSubscriberPrivateIdLocked}
              placeholder="Enter Subscriber Private Id"
              className={`w-full px-4 py-2 border border-gray-300 rounded-lg transition-all duration-200 ${
                isSubscriberPrivateIdLocked
                  ? "bg-gray-100 cursor-not-allowed"
                  : "focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              }`}
            />
            {errors?.np_subscriber_private_id && (
              <p className="text-sm text-red-600 mt-1">
                {errors.np_subscriber_private_id}
              </p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Unique Id <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="np_unique_id"
              aria-required="true"
              required
              value={form.np_unique_id}
              onChange={handleFieldChange}
              onBlur={() => onBlur("np_unique_id")}
              disabled={isUniqueIdLocked}
              placeholder="Enter Unique Id"
              className={`w-full px-4 py-2 border border-gray-300 rounded-lg transition-all duration-200 ${
                isUniqueIdLocked
                  ? "bg-gray-100 cursor-not-allowed"
                  : "focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              }`}
            />
            {errors?.np_unique_id && (
              <p className="text-sm text-red-600 mt-1">{errors.np_unique_id}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Signing Public Id <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="np_signing_public_id"
              aria-required="true"
              required
              value={form.np_signing_public_id}
              onChange={handleFieldChange}
              onBlur={() => onBlur("np_signing_public_id")}
              disabled={isSigningPublicIdLocked}
              placeholder="Enter Signing Public Id"
              className={`w-full px-4 py-2 border border-gray-300 rounded-lg transition-all duration-200 ${
                isSigningPublicIdLocked
                  ? "bg-gray-100 cursor-not-allowed"
                  : "focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              }`}
            />
            {errors?.np_signing_public_id && (
              <p className="text-sm text-red-600 mt-1">
                {errors.np_signing_public_id}
              </p>
            )}
          </div>
        </div>
      )}

      {showDomain && domainOptions && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Subscriber ID <span className="text-red-500">*</span>
              </label>
              <input
                name="npId"
                value={form.npId}
                onChange={handleFieldChange}
                disabled={form.npDomain === "Retail - MSN Provider"}
                onBlur={() => onBlur("npId")}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
              />
              {errors?.npId && (
                <p className="text-sm text-red-600 mt-1">{errors.npId}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Subscriber URL <span className="text-red-500">*</span>
              </label>
              <input
                name="npURL"
                value={form.npURL}
                onChange={handleFieldChange}
                disabled={form.npDomain === "Retail - MSN Provider"}
                onBlur={() => onBlur("npURL")}
                placeholder="https://"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
              />
              {errors?.npURL && (
                <p className="text-sm text-red-600 mt-1">{errors.npURL}</p>
              )}
            </div>
          </div>
        </>
      )}

      <InfoBox>
        Select an NP type, domain, and product(s) for your merchant. Additional
        fields will appear based on your selection.
      </InfoBox>
    </div>
  );
};

export default TenantInfoStep;
